import { useCallback, useEffect, useRef, useState } from "react";
import { Col, Row } from "react-grid-system";
import {
  GetAccountResponse,
  GetPhoneOptionsResponse,
  PhoneContact,
} from "../../../lib/api/models";
import { useGetInactivationChangeMessages } from "../../../lib/api/queries";
import { formatInCentralTime } from "../../../lib/formatting";
import StyledButton from "../../common/buttons/StyledButton";
import TextButton from "../../common/buttons/TextButton";
import StyledInput from "../../common/inputs/StyledInput";
import { UnsavedChangesModal } from "../../common/modals";
import PhoneNumberInput from "../../common/inputs/PhoneNumberInput";
import AutomatedSystemsConsentModal from "../AutomatedSystemsConsentModal";
import Memo from "../Memo";
import ModifyPhoneActivationStatus from "../ModifyPhoneActivationStatus";
import ModifyPhoneConsentInfo from "../ModifyPhoneConsentInfo";
import ModifyPhonePrimaryInfo from "../ModifyPhonePrimaryInfo";
import ModifyPhonesRestriction from "../ModifyPhonesRestriction";
import PartialSuccessMessagesModal from "../PartialSuccessMessagesModal";
import { PhoneNumberOverride, usePhoneNumberForm } from "../PhoneNumber.hooks";
import UpdatePhoneNumberModal from "../UpdatePhoneNumberModal";
import VerifyPhone from "../VerifyPhone";
import toolStyles from "../../../sharedStyles/Tool.module.css";
import styles from "./AddPhoneNumber.module.css";

const strings = {
  title: "Add Number",
  phoneNumber: "Phone Number*",
  extension: "Ext.",
  cancel: "Cancel",
  close: "Close",
  addMemo: "Add Memo",
  empty: "-",
  submit: "Submit",
  pending: "Pending",
  viewNumber: "View Number",
  editNumber: "Edit Number",
};

type Props = {
  account: GetAccountResponse;
  options: GetPhoneOptionsResponse;
  recordOfExistingPhoneContactsByPhoneNumber: Record<string, PhoneContact>;
  primaryNumber: string;
  secondaryNumber: string;
  onClickCancel: () => void;
  onClickClose: () => void;
  onClickEdit: (phoneContact: PhoneContact) => void;
  onClickView: (phoneContact: PhoneContact) => void;
  setToolInProgress: () => void;
  onDisplayGeneralErrors: (err: unknown) => void;
  referenceId?: string;
};

export default function AddPhoneNumber({
  account,
  options,
  recordOfExistingPhoneContactsByPhoneNumber,
  primaryNumber,
  secondaryNumber,
  onClickCancel,
  onClickClose,
  onClickEdit,
  onClickView,
  setToolInProgress,
  onDisplayGeneralErrors,
  referenceId,
}: Props) {
  const [isCloseWithoutSavingModelOpen, setIsCloseWithoutSavingModelOpen] =
    useState(false);
  const [isASCModalOpen, setASCModalOpen] = useState(false);
  const [isUpdatePhoneNumberModalOpen, setIsUpdatePhoneNumberModalOpen] =
    useState(false);

  const [
    formState,
    {
      generalDispatch,
      validationMessages,
      isLoading,
      checkOverridesAndSubmit,
      partialSuccessMessages,
      onClosePartialSuccessMessages,
      addOverride,
      clearOverrides,
      duplicatePhoneValidationError,
    },
  ] = usePhoneNumberForm(
    account.currentDwbuid,
    account.partyId,
    onClickCancel,
    onDisplayGeneralErrors,
    undefined,
    referenceId,
    recordOfExistingPhoneContactsByPhoneNumber,
  );

  const onClickSubmit = () => {
    const overrideToHandle = checkOverridesAndSubmit(
      primaryNumber,
      secondaryNumber,
    );
    switch (overrideToHandle) {
      case PhoneNumberOverride.ConsentStatus:
        setASCModalOpen(true);
        return;
      case PhoneNumberOverride.UpdatePhoneNumber:
        setIsUpdatePhoneNumberModalOpen(true);
        return;
    }
  };

  useEffect(() => {
    if (formState.isDirty) {
      setToolInProgress();
    }
  }, [formState.isDirty, setToolInProgress]);

  const todayInCentralTime = formatInCentralTime(new Date(), "MM/dd/yyyy");

  const {
    mutateAsync: getInactivationChangeMessages,
    data: inactivationChangeMessages,
  } = useGetInactivationChangeMessages();

  const consentRevoke = inactivationChangeMessages?.setConsentFields === "No";

  // For onGetInactivationChangeMessages, we want to always use the most up-to-date consent revoke value
  // But we don't want to re-run the request every time the consent revoke value changes
  const consentRevokeRef = useRef(consentRevoke);
  useEffect(() => {
    if (inactivationChangeMessages) {
      consentRevokeRef.current =
        inactivationChangeMessages.setConsentFields === "No";
    }
  }, [inactivationChangeMessages]);

  // For onGetInactivationChangeMessages, we want to always use the most up-to-date phone number
  // But we don't want to re-run the request every time the phone number changes
  const phoneNumberRef = useRef<string>(formState.phoneNumber);
  useEffect(() => {
    phoneNumberRef.current = formState.phoneNumber;
  }, [formState.phoneNumber]);

  const oldInactivationId = useRef<string>(
    formState.active ? "-1" : (formState.inactivationReasonId ?? "0"),
  );

  const isInitialChangeMessageLoad = useRef(true);
  const skipGetInactivationChangeMessages = useRef(false);
  const onGetInactivationChangeMessages = useCallback(async () => {
    try {
      // If this is the initial load for change messages for a new phone number,
      // we know that the results will be empty, so skip making the request
      if (isInitialChangeMessageLoad.current) {
        isInitialChangeMessageLoad.current = false;
        return;
      }

      if (skipGetInactivationChangeMessages.current) {
        skipGetInactivationChangeMessages.current = false;
        return;
      }

      const currentInactivationId = formState.active
        ? "-1"
        : (formState.inactivationReasonId ?? "0");

      const res = await getInactivationChangeMessages({
        dwbuid: account.currentDwbuid,
        originalInactivationId: "-1",
        oldInactivationId: oldInactivationId.current,
        newInactivationId: currentInactivationId,
        isRestricted: formState.restricted,
        phoneNumber: formState.phoneNumber,
        originalConsentStatus: null,
        newConsentStatus:
          formState.consentStatus ?? "PEC_NOT_SPECIFIED_OR_UNKNOWN",
        isEdit: false,
        agentSelectedPEC:
          !!formState.consentStatus && !consentRevokeRef.current,
      });

      if (res.setConsentFields === "Clear") {
        skipGetInactivationChangeMessages.current = true;
        generalDispatch({
          consentStatus: undefined,
        });
      } else if (res.setConsentFields === "No") {
        skipGetInactivationChangeMessages.current = true;
        generalDispatch({
          consentStatus: "PEC_NO",
        });
      }
    } catch (err) {
      onDisplayGeneralErrors(err);
    }
  }, [
    account.currentDwbuid,
    formState.active,
    formState.consentStatus,
    formState.inactivationReasonId,
    formState.phoneNumber,
    formState.restricted,
    generalDispatch,
    getInactivationChangeMessages,
    onDisplayGeneralErrors,
  ]);

  useEffect(() => {
    onGetInactivationChangeMessages();
  }, [onGetInactivationChangeMessages]);

  return (
    <>
      <Row>
        <Col md={2}>
          <div className={toolStyles.title}>{strings.title}</div>
          <TextButton
            className={styles.cancelButton}
            onClick={() =>
              formState.isDirty
                ? setIsCloseWithoutSavingModelOpen(true)
                : onClickCancel()
            }
          >
            {strings.cancel}
          </TextButton>
        </Col>
        <Col md={4}>
          <div className={styles.phoneNumberContainer}>
            <Row>
              <Col md={6}>
                <PhoneNumberInput
                  className={styles.phoneNumber}
                  label={strings.phoneNumber}
                  value={formState.phoneNumber}
                  onChange={(v) => generalDispatch({ phoneNumber: v })}
                  validationMessages={validationMessages?.phoneNumber}
                  disabled={isLoading}
                />
              </Col>
              <Col md={3}>
                <StyledInput
                  className={styles.extension}
                  label={strings.extension}
                  value={formState.extension}
                  onChange={(e) =>
                    generalDispatch({ extension: e.currentTarget.value })
                  }
                  validationMessages={validationMessages?.extension}
                  disabled={isLoading}
                  showPopover={false}
                />
              </Col>
            </Row>
          </div>
        </Col>
        <Col md={1} offset={{ md: 5 }}>
          <StyledButton
            className={styles.wFull}
            variant="secondary"
            onClick={onClickClose}
          >
            {strings.close}
          </StyledButton>
        </Col>
      </Row>
      <Row className={styles.minHeight16}>
        <Col>
          <ul className={styles.inactivationChangeMessages}>
            {inactivationChangeMessages?.messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
          {duplicatePhoneValidationError ? (
            <div className={styles.existingPhoneNumber}>
              <span className={styles.existingPhoneNumberText}>
                {duplicatePhoneValidationError.message}
              </span>
              <StyledButton
                className={styles.viewNumberButton}
                variant="transparent"
                onClick={() =>
                  onClickView(duplicatePhoneValidationError.phoneContact)
                }
              >
                {strings.viewNumber}
              </StyledButton>
              <StyledButton
                variant="transparent"
                onClick={() =>
                  onClickEdit(duplicatePhoneValidationError.phoneContact)
                }
              >
                {strings.editNumber}
              </StyledButton>
            </div>
          ) : null}
        </Col>
      </Row>
      <Row className={styles.rowSpacing}>
        <Col md={3}>
          <ModifyPhonePrimaryInfo
            account={account}
            options={options}
            formState={formState}
            generalDispatch={generalDispatch}
            validationMessages={validationMessages}
            disabled={isLoading}
          />
        </Col>
        <Col md={3}>
          <ModifyPhoneConsentInfo
            options={options}
            formState={formState}
            generalDispatch={generalDispatch}
            validationMessages={validationMessages}
            disabled={isLoading}
            consentRevoke={consentRevoke}
            updateAllConsentLabel={
              inactivationChangeMessages?.updateAllConsentLabel ?? false
            }
          />
        </Col>
        <Col md={6}>
          <Row className={styles.rowSpacing}>
            <Col>
              <VerifyPhone
                lastVerified={
                  formState.verified
                    ? `${todayInCentralTime} (pending)`
                    : strings.empty
                }
                lastCaptured={todayInCentralTime}
                firstCaptured={todayInCentralTime}
                onVerify={() =>
                  generalDispatch({
                    verified: true,
                  })
                }
                buttonLabel={formState.verified ? strings.pending : undefined}
                disabled={formState.verified}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <ModifyPhonesRestriction
                options={options}
                formState={formState}
                generalDispatch={generalDispatch}
                validationMessages={validationMessages}
                disabled={isLoading}
              />
            </Col>
            <Col md={6}>
              <ModifyPhoneActivationStatus
                options={options}
                formState={formState}
                generalDispatch={generalDispatch}
                validationMessages={validationMessages}
                disabled={isLoading}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Memo
            value={formState.memo}
            onChange={(v) => generalDispatch({ memo: v })}
            isLoading={isLoading}
            header={strings.addMemo}
            validationMessages={validationMessages?.memo}
          />
        </Col>
        <Col offset={{ md: 4 }} md={2} className={styles.submitButtonCol}>
          <StyledButton
            className={styles.wFull}
            variant="primary"
            onClick={onClickSubmit}
            isLoading={isLoading}
            disabled={!formState.isComplete || !formState.isDirty}
          >
            {strings.submit}
          </StyledButton>
        </Col>
      </Row>
      <UnsavedChangesModal
        isOpen={isCloseWithoutSavingModelOpen}
        onRequestClose={() => setIsCloseWithoutSavingModelOpen(false)}
        onConfirm={onClickCancel}
      />
      <AutomatedSystemsConsentModal
        isOpen={isASCModalOpen}
        consentYes={!formState.active && formState.consentStatus === "PEC_YES"}
        onRequestClose={() => {
          setASCModalOpen(false);
          generalDispatch({
            consentStatus: undefined,
          });
          clearOverrides();
        }}
        onAccept={() => {
          setASCModalOpen(false);
          addOverride(PhoneNumberOverride.ConsentStatus);
          onClickSubmit();
        }}
      />
      <PartialSuccessMessagesModal
        partialSuccessMessages={partialSuccessMessages}
        onRequestClose={onClosePartialSuccessMessages}
      />
      <UpdatePhoneNumberModal
        isOpen={isUpdatePhoneNumberModalOpen}
        phoneType={formState.updateFiservCode}
        prevNumber={
          formState.updateFiservCode === "UPDATE_FDR_HOME"
            ? primaryNumber
            : secondaryNumber
        }
        newNumber={formState.phoneNumber}
        onRequestClose={() => {
          setIsUpdatePhoneNumberModalOpen(false);
          clearOverrides();
        }}
        onSuccess={() => {
          setIsUpdatePhoneNumberModalOpen(false);
          addOverride(PhoneNumberOverride.UpdatePhoneNumber);
          onClickSubmit();
        }}
      />
    </>
  );
}
